import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type NoteWithAuthor = Tables<'notes'> & {
  profiles: { full_name: string | null } | null
}

interface NotesTimelineProps {
  entityType: 'order' | 'project'
  entityId: string
  organizationId: string
  projectId?: string
  showInternalOption?: boolean
}

export function NotesTimeline({ entityType, entityId, organizationId, projectId, showInternalOption = false }: NotesTimelineProps) {
  const { profile } = useAuth()
  const [notes, setNotes] = useState<NoteWithAuthor[]>([])
  const [loading, setLoading] = useState(true)
  const [content, setContent] = useState('')
  const [isInternal, setIsInternal] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    const fetchNotes = async () => {
      const { data } = await supabase
        .from('notes')
        .select('*, profiles(full_name)')
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .order('created_at', { ascending: true })
      setNotes((data as unknown as NoteWithAuthor[]) || [])
      setLoading(false)
    }
    fetchNotes()
  }, [entityType, entityId])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!content.trim() || !profile) return
    setSubmitting(true)

    const { data: newNote, error } = await supabase
      .from('notes')
      .insert({
        organization_id: organizationId,
        entity_type: entityType,
        entity_id: entityId,
        author_id: profile.id,
        content: content.trim(),
        is_internal: showInternalOption ? isInternal : false,
      })
      .select('*, profiles(full_name)')
      .single()

    if (!error && newNote) {
      setNotes(prev => [...prev, newNote as unknown as NoteWithAuthor])
      setContent('')
      setIsInternal(false)

      // Fire-and-forget activity log entry
      supabase.from('activity_log').insert({
        organization_id: organizationId,
        project_id: entityType === 'project' ? entityId : (projectId ?? null),
        actor_id: profile.id,
        activity_type: 'comment_added' as const,
        entity_type: entityType,
        entity_id: entityId,
        summary: `${profile.full_name || 'User'} added a note`,
      })
    }

    setSubmitting(false)
  }

  return (
    <div className={css.card}>
      <h3 style={{ marginBottom: 'var(--space-6)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        Notes
      </h3>

      {loading ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)' }}>Loading...</p>
      ) : notes.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', textAlign: 'center', padding: 'var(--space-4) 0' }}>
          No notes yet. Start the conversation below.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', marginBottom: 'var(--space-6)' }}>
          {notes.map(note => (
            <NoteItem key={note.id} note={note} currentUserId={profile?.id} />
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ borderTop: '1px solid var(--color-border-subtle)', paddingTop: 'var(--space-4)' }}>
        <textarea
          className={css.formTextarea}
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Add a note..."
          rows={3}
        />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'var(--space-3)' }}>
          <div>
            {showInternalOption && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', cursor: 'pointer' }}>
                <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} />
                Internal note (admin only)
              </label>
            )}
          </div>
          <button type="submit" className={`${css.btn} ${css.btnPrimary} ${css.btnSm}`} disabled={submitting || !content.trim()}>
            {submitting ? 'Posting...' : 'Add Note'}
          </button>
        </div>
      </form>
    </div>
  )
}

function NoteItem({ note, currentUserId }: { note: NoteWithAuthor; currentUserId?: string }) {
  return (
    <div style={{
      display: 'flex',
      gap: 'var(--space-3)',
      alignItems: 'flex-start',
      padding: 'var(--space-3) 0',
      borderBottom: '1px solid var(--color-border-subtle)',
    }}>
      <div style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
        marginTop: 6,
        background: note.is_internal ? 'var(--color-technical)' : 'var(--color-primary)',
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap', marginBottom: 'var(--space-1)' }}>
          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: note.author_id === currentUserId ? 'var(--color-primary-dark)' : 'var(--color-text)' }}>
            {note.profiles?.full_name || 'Unknown user'}
          </span>
          {note.is_internal && (
            <span style={{
              fontSize: '10px',
              fontWeight: 600,
              fontFamily: 'var(--font-mono)',
              padding: '1px 6px',
              borderRadius: 'var(--radius-2xl)',
              background: 'var(--color-technical-muted)',
              color: 'var(--color-technical)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}>
              Internal
            </span>
          )}
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            {new Date(note.created_at).toLocaleString()}
          </span>
        </div>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap', lineHeight: 1.5, margin: 0 }}>
          {note.content}
        </p>
      </div>
    </div>
  )
}
